export function parseColumnValue(columnValues, id, propertyIdToName = null) {
  if (!id) return null;
  const col = columnValues.find((c) => c.id === id);
  if (!col) return null;
  
  // For board relations, parse the value JSON to get linked_item_ids
  if (col.value && propertyIdToName) {
    try {
      const parsed = JSON.parse(col.value);
      // Board relation stores linked_item_ids as an array
      if (parsed.linked_item_ids && Array.isArray(parsed.linked_item_ids) && parsed.linked_item_ids.length > 0) {
        // Map linked item IDs to property names
        const propertyNames = parsed.linked_item_ids
          .map((itemId) => propertyIdToName[itemId])
          .filter(Boolean);
        if (propertyNames.length > 0) {
          return propertyNames.join(", ");
        }
      }
    } catch (e) {
      // Not JSON or parse error, continue to text
    }
  }
  
  // For lookup/mirror columns with dates, try parsing the value JSON
  // Lookup columns can have date values in JSON format
  // Try parsing value JSON even if text exists, as lookup columns might have better data in value
  if (col.value && col.value !== "null" && col.value !== "") {
    try {
      const parsed = JSON.parse(col.value);
      
      // Date columns in lookup can have { date: "YYYY-MM-DD" } format
      if (parsed.date) {
        return parsed.date;
      }
      
      // Or they might have the date directly as a string
      if (typeof parsed === 'string' && parsed.match(/^\d{4}-\d{2}-\d{2}/)) {
        return parsed;
      }
      
      // Some lookup columns might have the date in a different structure
      if (parsed.value && typeof parsed.value === 'string' && parsed.value.match(/^\d{4}-\d{2}-\d{2}/)) {
        return parsed.value;
      }
      
      // Lookup columns might return an array with date objects
      if (Array.isArray(parsed) && parsed.length > 0) {
        const firstItem = parsed[0];
        if (firstItem && firstItem.date) {
          return firstItem.date;
        }
        if (typeof firstItem === 'string' && firstItem.match(/^\d{4}-\d{2}-\d{2}/)) {
          return firstItem;
        }
        // Array might contain objects with date property
        if (firstItem && typeof firstItem === 'object') {
          if (firstItem.date) return firstItem.date;
          if (firstItem.value && typeof firstItem.value === 'string' && firstItem.value.match(/^\d{4}-\d{2}-\d{2}/)) {
            return firstItem.value;
          }
        }
      }
      
      // Lookup columns might have a nested structure with linked_item_ids (board relation lookup)
      // In this case, we should continue to text parsing
      if (parsed.linked_item_ids && Array.isArray(parsed.linked_item_ids)) {
        // This is a board relation lookup, not a date lookup - continue to text
      }
    } catch (e) {
      // Not JSON or parse error, continue to text
    }
  }
  
  // Use the human-readable text representation from monday
  // For lookup columns, text might be formatted differently, so try to extract date from text
  if (col.text) {
    // Try to extract date from text if it looks like a date string
    // Look for various date formats: YYYY-MM-DD, MM/DD/YYYY, DD/MM/YYYY, etc.
    const dateMatch = col.text.match(/\d{4}-\d{2}-\d{2}/) || 
                      col.text.match(/\d{2}\/\d{2}\/\d{4}/) ||
                      col.text.match(/\d{4}\/\d{2}\/\d{2}/);
    if (dateMatch) {
      let dateStr = dateMatch[0];
      // Convert MM/DD/YYYY or DD/MM/YYYY to YYYY-MM-DD
      if (dateStr.includes('/')) {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          // Assume MM/DD/YYYY format (most common)
          dateStr = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
        }
      }
      return dateStr;
    }
    // If text doesn't contain a date pattern, return it as-is (might be a property name or other value)
    return col.text;
  }
  
  return null;
}

export function parsePropertyNames(text) {
  if (!text) return ["(No property)"];
  // Handle both comma-separated and newline-separated property names
  return text
    .split(/[,\n]/)
    .map((t) => t.trim())
    .filter(Boolean);
}

export function isTaskDone(statusText, DONE_LABELS) {
  if (!statusText) return false;
  return DONE_LABELS.includes(statusText.trim().toLowerCase());
}

/**
 * Parse date from lookup/mirror column
 * Mirror columns copy values from other date columns, so they should have the same structure as date columns
 * The value should be in format: {"date": "YYYY-MM-DD"} or the text should contain the date
 */
export function parseLookupDateColumn(columnValues, columnId) {
  if (!columnId) return null;
  const col = columnValues.find((c) => c.id === columnId);
  if (!col) {
    return null;
  }
  
  // Mirror columns should work like regular date columns
  // First try to parse from value JSON - mirror columns should have { date: "YYYY-MM-DD" } format
  if (col.value && col.value !== "null" && col.value !== "") {
    try {
      const parsed = JSON.parse(col.value);
      
      // Standard date column format: { date: "YYYY-MM-DD" }
      if (parsed.date) {
        return parsed.date;
      }
      
      // Sometimes the date might be directly in the parsed value as a string
      if (typeof parsed === 'string' && parsed.match(/^\d{4}-\d{2}-\d{2}/)) {
        return parsed;
      }
      
      // Check for nested value structures
      if (parsed.value) {
        if (typeof parsed.value === 'string' && parsed.value.match(/^\d{4}-\d{2}-\d{2}/)) {
          return parsed.value;
        }
        if (parsed.value.date) {
          return parsed.value.date;
        }
      }
      
      // Array of dates (unlikely for mirror, but handle it)
      if (Array.isArray(parsed) && parsed.length > 0) {
        const firstItem = parsed[0];
        if (firstItem && firstItem.date) return firstItem.date;
        if (typeof firstItem === 'string' && firstItem.match(/^\d{4}-\d{2}-\d{2}/)) return firstItem;
      }
    } catch (e) {
      // Not JSON, continue to text parsing
    }
  }
  
  // Fall back to text parsing - mirror columns should display the date in text
  if (col.text) {
    // Try to extract date from text (mirror columns should show the date as text)
    const dateMatch = col.text.match(/\d{4}-\d{2}-\d{2}/) || 
                      col.text.match(/\d{2}\/\d{2}\/\d{4}/) ||
                      col.text.match(/\d{4}\/\d{2}\/\d{2}/);
    if (dateMatch) {
      let dateStr = dateMatch[0];
      if (dateStr.includes('/')) {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          // Assume MM/DD/YYYY format (most common)
          dateStr = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
        }
      }
      return dateStr;
    }
    // If text doesn't match date pattern, return null (not a valid date)
  }
  
  return null;
}

/**
 * Parse file column value from Monday.com API
 * Returns an array of file objects with name and url properties
 */
export function parseFileColumnValue(columnValues, columnId) {
  if (!columnId) return [];
  const col = columnValues.find((c) => c.id === columnId);
  if (!col || !col.value) return [];
  
  try {
    const parsed = JSON.parse(col.value);
    // File columns can have different structures
    if (Array.isArray(parsed)) {
      return parsed.map(file => {
        // Try multiple URL formats
        let url = file.url || file.assetUrl || file.asset_url || file.fileUrl || file.file_url;
        if (!url && file.assetId) {
          url = `https://files.monday.com/files/${file.assetId}`;
        }
        return {
          name: file.name || file.fileName || file.file_name || 'Unknown',
          url: url,
          assetId: file.assetId || file.asset_id || file.id
        };
      }).filter(file => file.url || file.assetId);
    } else if (parsed.files && Array.isArray(parsed.files)) {
      return parsed.files.map(file => {
        let url = file.url || file.assetUrl || file.asset_url || file.fileUrl || file.file_url;
        if (!url && file.assetId) {
          url = `https://files.monday.com/files/${file.assetId}`;
        }
        return {
          name: file.name || file.fileName || file.file_name || 'Unknown',
          url: url,
          assetId: file.assetId || file.asset_id || file.id
        };
      }).filter(file => file.url || file.assetId);
    } else if (parsed.assetId || parsed.url || parsed.assetUrl) {
      // Single file
      let url = parsed.url || parsed.assetUrl || parsed.asset_url || parsed.fileUrl || parsed.file_url;
      if (!url && parsed.assetId) {
        url = `https://files.monday.com/files/${parsed.assetId}`;
      }
      return [{
        name: parsed.name || parsed.fileName || parsed.file_name || 'Unknown',
        url: url,
        assetId: parsed.assetId || parsed.asset_id || parsed.id
      }].filter(file => file.url || file.assetId);
    }
  } catch (e) {
    // Not JSON, try to use text representation
    if (col.text) {
      // Sometimes file columns have text representation with file names
      return [];
    }
  }
  
  return [];
}

/**
 * Parse location column value from Monday.com API
 * Returns an object with lat and lng, or null if not found/invalid
 * Monday.com location columns typically store: { lat: number, lng: number } or { address: string, lat: number, lng: number }
 */
export function parseLocationColumn(columnValues, columnId) {
  if (!columnId) return null;
  const col = columnValues.find((c) => c.id === columnId);
  if (!col) return null;
  
  // Try parsing from value JSON first
  if (col.value && col.value !== "null" && col.value !== "") {
    try {
      const parsed = JSON.parse(col.value);
      
      // Monday.com location format: { lat: number, lng: number } or { address: string, lat: number, lng: number }
      if (typeof parsed.lat === 'number' && typeof parsed.lng === 'number') {
        return { lat: parsed.lat, lng: parsed.lng };
      }
      
      // Alternative format: { latitude: number, longitude: number }
      if (typeof parsed.latitude === 'number' && typeof parsed.longitude === 'number') {
        return { lat: parsed.latitude, lng: parsed.longitude };
      }
      
      // GeoJSON format: [lng, lat] array (GeoJSON uses lng, lat order)
      if (Array.isArray(parsed) && parsed.length >= 2 && typeof parsed[0] === 'number' && typeof parsed[1] === 'number') {
        // GeoJSON format is [lng, lat], so we need to swap them for lat, lng
        return { lat: parsed[1], lng: parsed[0] };
      }
      
      // Nested coordinates structure
      if (parsed.coordinates) {
        const coords = parsed.coordinates;
        if (Array.isArray(coords) && coords.length >= 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number') {
          // GeoJSON format [lng, lat]
          return { lat: coords[1], lng: coords[0] };
        }
        if (typeof coords.lat === 'number' && typeof coords.lng === 'number') {
          return { lat: coords.lat, lng: coords.lng };
        }
      }
    } catch (e) {
      // Not JSON or parse error, continue to text parsing
    }
  }
  
  // If value parsing failed, try extracting from text field
  // Monday.com sometimes includes coordinates in the text field
  if (col.text) {
    // Try to find coordinate pattern in text (e.g., "40.7128, -74.0060" or "lat: 40.7128, lng: -74.0060")
    const coordMatch = col.text.match(/(-?\d+\.?\d*)[,\s]+(-?\d+\.?\d*)/);
    if (coordMatch) {
      const lat = parseFloat(coordMatch[1]);
      const lng = parseFloat(coordMatch[2]);
      if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        return { lat, lng };
      }
    }
  }
  
  return null;
}

